import { FC, useState } from 'react';
import styles from './SideBar.module.scss';
import { GoADetails, GoAIconButton } from '@abgov/react-components';
import DetailsContainer from './DetailsContainer';
import { IFlightReport } from '../../../../model/IFlightReport';
import SideBarHeader from './SideBarHeader';
import DailyWorkPeriod from './DailyWorkPeriod';
import Summary from './Summary';
import HiringDetails from './HiringDetails';
import ActionBar from './ActionBar';
import { ICorporateRegion } from '../../../../model/ICorporateRegion';
import { IPaginationResult } from '../../../../model/search/IPaginationResult';
import FlightReportStatus from '../../../../model/enum/FlightReportStatus';
import { AuthenticationService } from '../../../../services/authentication/AuthenticationService';
import { useAuth } from 'react-oidc-context';
const { container, toggleButton, content, header } = styles;
export type SectionKey = 'daily' | 'summary';
interface ISideBarProps {
  isNew: boolean;
  ruleCode: string;
  flightReport: IFlightReport;
  onSave: (ignoreWarning: boolean) => Promise<boolean>;
  onCancel: () => void;
  onChange: (updatedFields: Partial<IFlightReport>) => void;
  onNoFlyDay: (value: boolean) => void;
  onChangeUnservicableReport: (value: boolean) => void;
  corporateRegionPaginationResult: IPaginationResult<ICorporateRegion>;
  isChildDataValid: boolean;
}
const SideBar: FC<ISideBarProps> = ({
  isNew,
  ruleCode,
  flightReport,
  onSave,
  onCancel,
  onChange,
  onNoFlyDay,
  onChangeUnservicableReport,
  corporateRegionPaginationResult,
  isChildDataValid,
}) => {
  const auth = useAuth();
  const [isOpen, setIsOpen] = useState(true);
  const [editSection, setEditSection] = useState<SectionKey | null>(() => {
    return isNew ? 'summary' : null;
  });

  const toggle = () => {
    setIsOpen(!isOpen);
  };

  const CanEditSummary = () => {
    if (flightReport.status === FlightReportStatus.processed) {
      return false;
    }
    const isDispatcher =
      AuthenticationService.hasResourceRole('FlightReports', 'p_Aviat_FlightRpt_W', auth.user?.access_token as string) &&
      AuthenticationService.hasResourceRole('FlightReports', 'Avi_FlightRptDisps_W', auth.user?.access_token as string);
    if (isDispatcher && (flightReport.status === FlightReportStatus.signedOff || flightReport.status === FlightReportStatus.approved)) {
      return false;
    }

    return true;
  };
  return (
    <div className={container}>
      <div className={toggleButton}>
        <GoAIconButton icon={isOpen ? 'arrow-back' : 'arrow-forward'} onClick={toggle}></GoAIconButton>
      </div>

      {isOpen && (
        <div className={content}>
          <div>
            <ActionBar
              canSignOff={editSection === null}
              flightReport={flightReport}
              corporateRegionResult={corporateRegionPaginationResult}
              onChange={onChange}
            />
          </div>
          <div className={header}>
            <SideBarHeader flightReport={flightReport} />
          </div>
          {flightReport.flightReportId && (
            <GoADetails heading='Daily work period' open={true}>
              {CanEditSummary() && (
                <DetailsContainer
                  section={'daily'}
                  activeSection={editSection}
                  setActiveSection={setEditSection}
                  onSave={async () => {
                    if (await onSave(false)) setEditSection(null);
                  }}
                  onCancel={() => {
                    onCancel();
                    setEditSection(null);
                  }}
                >
                  <DailyWorkPeriod flightReport={flightReport} isEdit={editSection === 'daily'} onChange={onChange} />
                </DetailsContainer>
              )}
              {!CanEditSummary() && <DailyWorkPeriod flightReport={flightReport} isEdit={editSection === 'daily'} onChange={onChange} />}
            </GoADetails>
          )}
          <GoADetails heading='Flight report summary' open={true}>
            {CanEditSummary() && (
              <DetailsContainer
                section={'summary'}
                activeSection={editSection}
                setActiveSection={setEditSection}
                saveDisabled={!isChildDataValid}
                onSave={async () => {
                  if (await onSave(false)) setEditSection(null);
                }}
                onCancel={() => {
                  onCancel();
                  setEditSection(null);
                }}
              >
                <Summary
                  ruleCode={ruleCode}
                  flightReport={flightReport}
                  isEdit={editSection === 'summary'}
                  onChange={onChange}
                  onNoFlyDay={onNoFlyDay}
                  corporateRegionPaginationResult={corporateRegionPaginationResult}
                  onChangeUnservicableReport={onChangeUnservicableReport}
                />
              </DetailsContainer>
            )}
            {!CanEditSummary() && (
              <Summary
                ruleCode={ruleCode}
                flightReport={flightReport}
                isEdit={editSection === 'summary'}
                onChange={onChange}
                onNoFlyDay={onNoFlyDay}
                corporateRegionPaginationResult={corporateRegionPaginationResult}
                onChangeUnservicableReport={onChangeUnservicableReport}
              />
            )}
          </GoADetails>
          <GoADetails heading='Hiring information' open={true}>
            <HiringDetails flightReport={flightReport} />
          </GoADetails>
        </div>
      )}
    </div>
  );
};
export default SideBar;
